/**
 * Zahlen für die Werte einer Anlage (HM-012).
 *
 * **Eine Größenordnung für das ganze Bild.** Die Achse eines Diagramms und die
 * Kacheln darüber zeigen dieselbe Reihe; stünde die eine in Wh und die andere
 * in kWh, läse man 850 neben 0,9 und hielte beides für verschiedene Zahlen.
 * Ein Haushalt rechnet in Kilowattstunden — die Stufe steht deshalb fest und
 * wird nicht je Wert gewählt, wie es `formatValue` sonst tut.
 *
 * Verhältnisse werden als Prozent gezeigt, ohne Nachkommastelle: „71 %
 * autark" ist eine Aussage, „71,3 %" eine Genauigkeit, die vier
 * Viertelstundenzähler nicht hergeben.
 */
import { formatRatio, formatValue, NARROW_NBSP } from "@hausmeter/i18n";
import type { ValueFormat } from "@hausmeter/ui/chart";

export const ENERGY_UNIT = "kWh";

/**
 * Energie in kWh nach dem Standard des Produkts (`standardFractionDigits` in
 * `@hausmeter/i18n`): unter 10 kWh drei Stellen, unter 100 zwei, darüber eine,
 * Nullen gekürzt. Eine andere Stellenzahl nur ausdrücklich — eine Achse, ein
 * Zählerstand.
 */
export function formatEnergy(wh: number, maximumFractionDigits?: number): string {
  return formatValue(wh, "Wh", undefined, {
    fixedUnit: ENERGY_UNIT,
    ...(maximumFractionDigits === undefined ? {} : { maximumFractionDigits }),
  }).text;
}

/**
 * **Eine Summe auf einer Übersicht** (UI-Durchsicht U9): unter 100 kWh eine
 * Stelle, darüber keine. „4,326 kWh“ als Tagesverbrauch täuscht eine Genauigkeit
 * vor, nach der niemand fragt, und macht die Karten unruhig; der genaue Wert
 * steht im Ablesefenster und am Zähler. Zählerstände bleiben bei `formatEnergy`.
 */
export function formatEnergySummary(wh: number): string {
  return formatEnergy(wh, Math.abs(wh) < 100_000 ? 1 : 0);
}

/** Zahl und Einheit zusammen, mit schmalem Leerzeichen. */
export function formatEnergyFull(wh: number): string {
  return `${formatEnergy(wh)}${NARROW_NBSP}${ENERGY_UNIT}`;
}

/** Die Achse mit bis zu drei Stellen: Ihre Kanten sind glatte Zahlen. */
export const ENERGY_FORMAT: ValueFormat = {
  unit: ENERGY_UNIT,
  axis: (wh) => formatEnergy(wh, 3),
  value: (wh) => formatEnergyFull(wh),
};

export function formatPercent(ratio: number): string {
  return formatRatio(ratio).text;
}

/** Die Achse trägt nur die Zahl („80"), das Prozentzeichen steht einmal darüber. */
export const RATIO_FORMAT: ValueFormat = {
  unit: "%",
  axis: (ratio) => new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(ratio * 100),
  value: (ratio) => formatPercent(ratio),
};

/** „bei 83 % Abdeckung" — oder nichts, wenn das Fenster vollständig ist. */
export function coverageNote(coverage: number): string | null {
  if (coverage >= 0.995) return null;
  return `Daten für ${formatPercent(coverage)} des Zeitraums`;
}

type Quality = "measured" | "interpolated" | "estimated" | "suspect";

/**
 * Was unter einer Summe steht: die Abdeckung, wenn sie unvollständig ist, und
 * „mit Ersatzwerten", wenn welche darin stecken (HM-115) — oder nichts.
 *
 * **Beides sind verschiedene Aussagen.** Eine Teilsumme ist kleiner als die
 * Wirklichkeit; eine Summe mit Ersatzwerten ist in der Menge richtig, nur ihre
 * Verteilung auf die Viertelstunden ist geschätzt — und damit alles, was je
 * Viertelstunde verteilt wurde, etwa ein PV-Anteil.
 */
export function totalNote(total: { readonly coverage: number; readonly quality: Quality }): string | null {
  const parts = [
    coverageNote(total.coverage),
    total.quality === "measured" ? null : total.quality === "suspect" ? "mit unplausiblen Werten" : "mit Ersatzwerten",
  ].filter((part): part is string => part !== null);
  return parts.length === 0 ? null : parts.join(" · ");
}

/**
 * Nur der Status einer Summe — „mit Ersatzwerten" oder nichts (HM-138). Für die
 * Kacheln und Verhältnisse unter „Strom & Erzeugung", die nach dem Entwurf keine
 * Abdeckung nennen: Wie viel des Zeitraums vorliegt, zeigt dort das Diagramm
 * (blasse Balken, Lücken); dass Werte ersetzt sind, zeigt es nicht in der Summe.
 */
export function qualityNote(total: { readonly quality: Quality }): string | null {
  return total.quality === "measured"
    ? null
    : total.quality === "suspect"
      ? "mit unplausiblen Werten"
      : "mit Ersatzwerten";
}

const QUALITY_ORDER: readonly Quality[] = ["measured", "interpolated", "estimated", "suspect"];

/** Der unsicherere zweier Status — für eine Summe, die hier aus zwei Summen entsteht. */
export function lessCertainQuality(left: Quality, right: Quality): Quality {
  return QUALITY_ORDER.indexOf(left) >= QUALITY_ORDER.indexOf(right) ? left : right;
}

/** Die Einheit einer Leistungsachse — eine Stufe für das ganze Diagramm. */
export const POWER_UNIT = "kW";

/** Leistung in fester kW-Stufe — nur für eine Achse, deren Kanten vergleichbar sein müssen. */
export function formatPower(w: number, maximumFractionDigits = 1): string {
  return formatValue(w, "W", undefined, { fixedUnit: POWER_UNIT, maximumFractionDigits }).text;
}

/**
 * **Ein einzelner Leistungswert wählt seine Stufe selbst** — der Standard des
 * Produkts aus `@hausmeter/i18n`: unter 1 kW ganze W, dann 1,xx kW, 10,x kW und
 * ab 100 kW ganze kW.
 */
export function powerParts(w: number): { readonly text: string; readonly unit: string } {
  const { text, unit } = formatValue(w, "W");
  return { text, unit };
}

/** Zahl und Einheit zusammen, mit schmalem Leerzeichen. */
export function formatPowerFull(w: number): string {
  const { text, unit } = powerParts(w);
  return `${text}${NARROW_NBSP}${unit}`;
}

/** Die Achse bleibt in kW, das Ablesefenster wählt die Stufe je Wert. */
export const POWER_FORMAT: ValueFormat = {
  unit: POWER_UNIT,
  axis: (w) => formatPower(w, 1),
  value: (w) => formatPowerFull(w),
};

/**
 * Ein Punkt des Vertrags als Punkt des Diagramms (HM-105, HM-115).
 *
 * Abdeckung und Status gehen mit: Ein Tag mit 94 von 96 Viertelstunden ist eine
 * Teilsumme, eine Viertelstunde mit `q: "estimated"` ein Ersatzwert, und das
 * Diagramm zeichnet und beschriftet beides so. Wer sie hier fallen ließe,
 * zeigte einen halben Tag als ganzen und eine Schätzung als Messung.
 */
export function chartPoint(point: { t: string; v: number; c?: number; q?: Quality }): {
  at: number;
  value: number;
  coverage?: number;
  quality?: Quality;
} {
  return {
    at: new Date(point.t).getTime(),
    value: point.v,
    ...(point.c === undefined ? {} : { coverage: point.c }),
    ...(point.q === undefined || point.q === "measured" ? {} : { quality: point.q }),
  };
}
